import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { PatternFormat } from "react-number-format";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  closeAddAccountAndPayDialog,
  changeProjectData,
  fetchProjects,
  openProjectDrawer,
  setEditingBillingAddress,
  updateProjectPaymentMethod,
} from "../../../../redux/slices/projectsSlice";
import { chargeInvoice } from "../../../../redux/slices/invoicesSlice";
import { useCallback, useEffect, useMemo, useState } from "react";

const REQUIRED_BILLING_ADDRESS_FIELDS = [
  "billAddress1",
  "billAddressCity",
  "billAddressState",
  "billAddressCountry",
  "billAddressZip",
] as const;

const AddAccountAndPayDialog = () => {
  const {
    selectedProject,
    addAccountAndPayDialogOpen,
    changes,
    updateProjectPaymentMethodStatus,
    updateProjectPaymentMethodError,
  } = useAppSelector((state) => state.projects) as any;

  const { selectedInvoice, invoiceChargeStatus } = useAppSelector(
    (state) => state.invoices
  ) as any;

  const dispatch = useAppDispatch();
  const activeSource = useAppSelector((state) => state.source.source);
  const [confirmAccount, setConfirmAccount] = useState("");
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (!addAccountAndPayDialogOpen) return;
    setConfirmAccount("");
    setAuthorized(false);
  }, [addAccountAndPayDialogOpen]);

  // Put the invoice balance in the amount field the first time the dialog
  // opens; the client can change it before paying.
  useEffect(() => {
    if (!addAccountAndPayDialogOpen || !selectedInvoice || changes?.amount) return;
    const due =
      selectedInvoice?.calculatedAmountDue ??
      selectedInvoice?.amountDue ??
      selectedInvoice?.amount;
    if (due != null && Number(due) > 0) {
      dispatch(
        changeProjectData({
          name: "amount",
          value: Number(due).toFixed(2),
        })
      );
    }
  }, [
    addAccountAndPayDialogOpen,
    selectedInvoice,
    changes?.amount,
    dispatch,
  ]);

  const billingAddressMissingFields = useMemo(() => {
    const billingAddress = selectedProject?.billingAddress;

    return REQUIRED_BILLING_ADDRESS_FIELDS.filter((field) => {
      const value = billingAddress?.[field];
      return typeof value !== "string" || value.trim() === "";
    });
  }, [selectedProject]);

  const hasValidBillingAddress = billingAddressMissingFields.length === 0;

  // A complete billing address is Bill.com's requirement for a charge, not
  // Intuit's: an ACH debit is taken without one, so on the Intuit rail the
  // warning and the block it causes are skipped, and the fields stay checked on
  // the other rail where a charge really depends on them.
  //
  // The rail is read from the record being paid, not from the portal's switch:
  // an invoice and its customer each carry the book they came from, and a
  // payment follows the invoice. Going by the switch meant the warning stayed
  // put whenever the portal happened to be showing the other side.
  const paymentSource =
    selectedInvoice?.source || selectedProject?.source || activeSource;
  const billingAddressBlocks =
    paymentSource !== "ies" && !hasValidBillingAddress;

  const hasBillingAddressValidationError =
    typeof updateProjectPaymentMethodError === "string" &&
    updateProjectPaymentMethodError.toLowerCase().includes("billing address");

  const handleClose = useCallback(() => {
    dispatch(closeAddAccountAndPayDialog());
  }, [dispatch]);

  const handleEditBillingAddress = useCallback(() => {
    if (!selectedProject) {
      return;
    }

    dispatch(closeAddAccountAndPayDialog());
    dispatch(openProjectDrawer(selectedProject));
    dispatch(setEditingBillingAddress(true));
  }, [dispatch, selectedProject]);

  const onChange = (e: any) => {
    const { name, value } = e.target;
    dispatch(
      changeProjectData({
        name,
        value,
      })
    );
  };

  const handleChangeAmount = (event: any) => {
    const { name, value } = event.target;

    let cleaned = value.replace(/[^\d.]/g, "");

    cleaned = cleaned.replace(/(\..*)\./g, "$1");

    if (cleaned.includes(".")) {
      const [intPart, decPart] = cleaned.split(".");
      cleaned = intPart + "." + (decPart ? decPart.slice(0, 2) : "");
    }

    if (
      cleaned.startsWith("0") &&
      cleaned.length > 1 &&
      !cleaned.startsWith("0.")
    ) {
      cleaned = cleaned.replace(/^0+/, "") || "0";
    }

    const amount = cleaned === "" ? 0 : cleaned;
    dispatch(
      changeProjectData({
        name,
        value: amount,
      })
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (
      !selectedProject?.providerId ||
      !selectedInvoice?.customerId ||
      !selectedInvoice?.id ||
      billingAddressBlocks ||
      !accountReady
    ) {
      return;
    }

    const amount = changes?.amount;

    try {
      const paymentMethodResponse = await dispatch(
        updateProjectPaymentMethod({
        projectId: selectedProject.providerId,
          ...changes,
          isSavings: changes?.isSavings || false,
          isPersonalAcct: changes?.isPersonalAcct || false,
        })
      ).unwrap();

      // The drawer decides between "Add Bank Account" and "Select Account"
      // from the bankAccounts carried by the projects list. That list is
      // fetched once when the client home loads, so without this refresh the
      // drawer still saw no account after the save and offered this form
      // again — which is how the same account was stored twice.
      dispatch(fetchProjects());

      await dispatch(
        chargeInvoice({
          customerId: selectedInvoice.customerId,
          invoiceId: selectedInvoice.id,
          accountId: paymentMethodResponse?.accountId,
          amount,
        })
      ).unwrap();

      dispatch(closeAddAccountAndPayDialog());
    } catch {
      // Global snackbar handlers show the API error message.
    }
  };

  const accountMatches =
    !!changes?.accountNumber && changes.accountNumber === confirmAccount;
  const accountReady =
    !!changes?.accountName &&
    !!changes?.routingNumber &&
    !!changes?.accountNumber &&
    !!changes?.phone &&
    authorized &&
    accountMatches;

  const baseDisabled =
    updateProjectPaymentMethodStatus === "loading" ||
    invoiceChargeStatus === "loading" ||
    !selectedProject?.providerId ||
    !selectedInvoice?.customerId ||
    !selectedInvoice?.id ||
    billingAddressBlocks;

  const isSubmitDisabled = baseDisabled || !accountReady || !changes?.amount;
  const isSaveDisabled = baseDisabled || !accountReady;

  const handleSaveOnly = async (event: any) => {
    event.preventDefault();
    if (isSaveDisabled) return;
    try {
      await dispatch(
        updateProjectPaymentMethod({
          projectId: selectedProject.providerId,
          ...changes,
          isSavings: changes?.isSavings || false,
          isPersonalAcct: changes?.isPersonalAcct || false,
        })
      ).unwrap();
      dispatch(fetchProjects());
      dispatch(closeAddAccountAndPayDialog());
    } catch {
      // Global snackbar handlers show the API error message.
    }
  };

  return (
    <Dialog
      open={addAccountAndPayDialogOpen}
      maxWidth="xs"
      component={"form"}
      onSubmit={handleSubmit}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 0,
          padding: 1,
        },
        "& .MuiOutlinedInput-input": {
          p: "4.5px 14px",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: 0,
        },
        "& .MuiTextField-root": {
          mt: 0,
        },
        "& .MuiButton-root": {
          borderRadius: 0,
          p: "2.75px 15px",
        },
      }}
    >
      <DialogTitle>{selectedProject?.name}</DialogTitle>
      <DialogContent>
        <Typography
          style={{
            fontFamily: "Brandon_Grotesque_bold",
          }}
        >
          Add Bank Account and Pay
        </Typography>
        {billingAddressBlocks || hasBillingAddressValidationError ? (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 0 }}>
            <Box>
              <Typography variant="body2">
                A complete billing address is required before adding a bank
                account or charging this invoice.
              </Typography>
              {billingAddressMissingFields.length > 0 ? (
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  Missing fields: {billingAddressMissingFields.join(", ")}
                </Typography>
              ) : null}
              <Button
                variant="outlined"
                color="warning"
                onClick={handleEditBillingAddress}
                sx={{ mt: 1.5 }}
              >
                Update Billing Address
              </Button>
            </Box>
          </Alert>
        ) : null}
        <Grid
          sx={{
            mt: 1,
          }}
          container
        >
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Name on Account
            </InputLabel>
            <TextField
              id="accountName"
              name="accountName"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter the name on the account"
              value={changes?.accountName || ""}
              onChange={onChange}
            />
          </Grid>
          <Grid size={12}>
            <InputLabel sx={{ fontSize: 14 }}>Account Type</InputLabel>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {[
                { label: "Checking", value: false },
                { label: "Savings", value: true },
              ].map((option) => (
                <Button
                  key={option.label}
                  type="button"
                  size="small"
                  variant={
                    Boolean(changes?.isSavings) === option.value
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() =>
                    dispatch(
                      changeProjectData({
                        name: "isSavings",
                        value: option.value,
                      })
                    )
                  }
                  sx={{ borderRadius: 0, flex: 1 }}
                  disabled={
                    updateProjectPaymentMethodStatus === "loading" ||
                    invoiceChargeStatus === "loading"
                  }
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Routing Number
            </InputLabel>
            <TextField
              id="routingNumber"
              name="routingNumber"
              variant="outlined"
              size="small"
              fullWidth
              //   type="number"
              margin="normal"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter your routing number"
              value={changes?.routingNumber || ""}
              onChange={onChange}
            />
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Account Number
            </InputLabel>
            <TextField
              id="accountNumber"
              name="accountNumber"
              variant="outlined"
              size="small"
              fullWidth
              // type="number"
              margin="normal"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter full account number"
              value={changes?.accountNumber || ""}
              onChange={onChange}
            />
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="confirmAccount" sx={{ fontSize: 14 }}>
              Confirm Account Number
            </InputLabel>
            <TextField
              id="confirmAccount"
              name="confirmAccount"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={
                !!confirmAccount &&
                confirmAccount !== (changes?.accountNumber || "")
              }
              helperText={
                confirmAccount &&
                confirmAccount !== (changes?.accountNumber || "")
                  ? "The account numbers do not match."
                  : ""
              }
              placeholder="Re-enter the account number"
              value={confirmAccount}
              onChange={(event) => setConfirmAccount(event.target.value)}
            />
          </Grid>
          <Grid size={12}>
            {/* Intuit requires a phone number on the bank account it stores, and
                falls back to the customer record — which a client may not have.
                Asking here is what lets them add an account at all. */}
            <InputLabel htmlFor="phone" sx={{ fontSize: 14 }}>
              Phone Number
            </InputLabel>
            <PatternFormat
              customInput={TextField}
              format="(###) ###-####"
              id="phone"
              name="phone"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="tel"
              inputMode="tel"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={!!updateProjectPaymentMethodError}
              placeholder="(555) 123-4567"
              value={changes?.phone || ""}
              onValueChange={(values) =>
                dispatch(changeProjectData({ name: "phone", value: values.value }))
              }
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={authorized}
                  onChange={(event) => setAuthorized(event.target.checked)}
                  disabled={
                    updateProjectPaymentMethodStatus === "loading" ||
                    invoiceChargeStatus === "loading"
                  }
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: 12 }}>
                  I authorize The Pinnacle Companies to debit this account for
                  the amount shown.
                </Typography>
              }
            />
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="amount" sx={{ fontSize: 14 }}>
              Amount to Pay
            </InputLabel>
            <TextField
              id="amount"
              name="amount"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              disabled={
                updateProjectPaymentMethodStatus === "loading" ||
                invoiceChargeStatus === "loading"
              }
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter the amount to pay"
              value={changes?.amount || ""}
              onChange={handleChangeAmount}
              type="number"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="button"
          variant="outlined"
          onClick={handleSaveOnly}
          disabled={isSaveDisabled}
        >
          Save without paying
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={isSubmitDisabled}
        >
          Add & Pay
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccountAndPayDialog;
